import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { X, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Info } from 'lucide-react';
import ToggleSwitch from '../common/ToggleSwitch';
import ModalShell, { ModalFooter } from '../common/ModalShell';
import { FIELD_TYPE_ICON } from './fieldTypeIcons';
import { createCheckSet, updateCheckSet } from '../../redux/checkSetSlice';
import {
    FIELD_TYPES, LIMITS, collapse, emptyField, emptySetDraft, setToDraft, draftToBody,
    validateSetDraft, hasSetErrors,
} from '../../utils/checkSets';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// THE CHECK-SET BUILDER — one editor for a doctor's own sets and the
// hospital's (ownerType 'tenant'). Every field is the user's: label, type
// (number / text / choose one / choose several / date / yes-no), a unit for a
// number, the options of a choice, mandatory or not. Nothing is offered
// pre-filled (owner, 26 Sep 2026: nothing speciality-specific).
//
//   doctor set    + "Required in every visit" (the completion gate)
//   hospital set  + an optional free "Meant for" label (groups the list only)
//   both          + "Staff may record this set"
//
// Existing fields keep their key (old readings line up with them); a removed
// field disappears from the set, and readings already taken keep it in their
// own snapshot. The server re-checks every rule and its refusal is shown next
// to the field it names (`index`).

function CheckSetEditor({ isOpen, ownerType = 'doctor', doctorId = null, set = null, onClose, onSaved }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const saving = useSelector((state) => state.checkSets?.saving);
    const isNew = !set;
    const [draft, setDraft] = useState(emptySetDraft);
    const [initialKey, setInitialKey] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [optionInputs, setOptionInputs] = useState({});
    const nameRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const next = set ? setToDraft(set) : emptySetDraft();
        setDraft(next);
        setInitialKey(JSON.stringify(draftToBody(next, ownerType)));
        setShowErrors(false);
        setServerErrors({});
        setServerError('');
        setOptionInputs({});
        setTimeout(() => nameRef.current?.focus(), 0);
    }, [isOpen, set, ownerType]);

    const errors = useMemo(() => validateSetDraft(draft, { ownerType }), [draft, ownerType]);
    const dirty = isOpen && JSON.stringify(draftToBody(draft, ownerType)) !== initialKey;

    const requestClose = async () => {
        if (saving) return;
        if (dirty) {
            const res = await Swal.fire({
                title: t('checkSets.discard_title'),
                text: t('checkSets.discard_text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('checkSets.discard_confirm'),
                cancelButtonText: t('checkSets.keep_editing'),
                confirmButtonColor: '#d33',
                cancelButtonColor: '#9ca3af',
            });
            if (!res?.isConfirmed) return;
        }
        onClose?.();
    };

    const requestCloseRef = useRef(requestClose);
    useEffect(() => { requestCloseRef.current = requestClose; });
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') requestCloseRef.current?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen]);

    if (!isOpen || typeof document === 'undefined') return null;

    const patch = (changes) => {
        setDraft((prev) => ({ ...prev, ...changes }));
        setServerError('');
    };
    const patchField = (index, changes) => {
        setDraft((prev) => ({ ...prev, fields: prev.fields.map((f, i) => (i === index ? { ...f, ...changes } : f)) }));
        setServerErrors((prev) => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };
    const addField = () => {
        if (draft.fields.length >= LIMITS.fieldsMax) return;
        setDraft((prev) => ({ ...prev, fields: [...prev.fields, emptyField()] }));
    };
    const removeField = (index) => {
        setDraft((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
        setServerErrors({});
    };
    const moveField = (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= draft.fields.length) return;
        setDraft((prev) => {
            const fields = [...prev.fields];
            [fields[index], fields[target]] = [fields[target], fields[index]];
            return { ...prev, fields };
        });
        setServerErrors({});
    };
    const addOption = (index) => {
        const field = draft.fields[index];
        const value = collapse(optionInputs[field.uid]).slice(0, LIMITS.option);
        if (!value) return;
        if ((field.options || []).some((o) => o.toLowerCase() === value.toLowerCase())) {
            setOptionInputs((prev) => ({ ...prev, [field.uid]: '' }));
            return;
        }
        if ((field.options || []).length >= LIMITS.optionsMax) return;
        patchField(index, { options: [...(field.options || []), value] });
        setOptionInputs((prev) => ({ ...prev, [field.uid]: '' }));
    };
    const removeOption = (index, option) => {
        const field = draft.fields[index];
        patchField(index, { options: (field.options || []).filter((o) => o !== option) });
    };

    const save = async () => {
        if (saving) return;
        setShowErrors(true);
        if (hasSetErrors(errors)) return;
        const body = draftToBody(draft, ownerType);
        setServerError('');
        setServerErrors({});
        let res;
        if (isNew) {
            const createBody = { ...body, ownerType };
            if (ownerType === 'doctor' && doctorId) createBody.doctorId = doctorId;
            res = await dispatch(createCheckSet({ body: createBody }));
        } else {
            res = await dispatch(updateCheckSet({ setId: set.setId, changes: body }));
        }
        const ok = isNew ? createCheckSet.fulfilled.match(res) : updateCheckSet.fulfilled.match(res);
        if (ok) {
            toast.success(t('checkSets.saved'));
            onSaved?.(res.payload?.set || null);
            onClose?.();
            return;
        }
        const refusal = res.payload || {};
        const message = serverReason(refusal, t('checkSets.save_failed'), i18n.language);
        if (typeof refusal.index === 'number' && draft.fields[refusal.index]) {
            setServerErrors({ [refusal.index]: message });
        } else {
            setServerError(message);
        }
    };

    const fieldErr = (index, part) => {
        if (!showErrors) return '';
        const key = errors.byIndex?.[index]?.[part];
        return key ? t(`checkSets.${key}`) : '';
    };
    const titleKey = isNew ? (ownerType === 'tenant' ? 'editor_new_hospital_title' : 'editor_new_title') : 'editor_edit_title';
    const input = 'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD]';

    return createPortal(
        // Click on the dimmed page asks to close, as before the shared frame.
        <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) requestClose(); }}>
            <ModalShell
                title={t(`checkSets.${titleKey}`)}
                onClose={requestClose}
                closeLabel={t('checkSets.close')}
                size="3xl"
                zClass="z-[60]"
                testId="check-set-editor-root"
                closeTestId="check-set-editor-close"
                contentTestId="check-set-editor"
                footer={(
                    <ModalFooter
                        onCancel={requestClose}
                        cancelLabel={t('checkSets.cancel')}
                        onSubmit={save}
                        submitLabel={saving ? t('checkSets.saving') : t('checkSets.save_set')}
                        busy={saving}
                        cancelTestId="check-set-cancel"
                        submitTestId="check-set-save"
                    />
                )}
            >
                <div className="space-y-5">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className={ownerType === 'tenant' ? '' : 'sm:col-span-2'}>
                            <label htmlFor="check-set-name" className="mb-1 block text-xs font-semibold text-gray-600">{t('checkSets.name_label')}</label>
                            <input
                                id="check-set-name"
                                ref={nameRef}
                                data-testid="check-set-name"
                                type="text"
                                maxLength={LIMITS.name}
                                value={draft.name}
                                placeholder={t('checkSets.name_placeholder')}
                                onChange={(e) => patch({ name: e.target.value.replace(/[<>]/g, '') })}
                                aria-invalid={Boolean(showErrors && errors.name)}
                                className={`${input} ${showErrors && errors.name ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                            />
                            {showErrors && errors.name && <p data-testid="check-set-name-error" className="mt-1 text-xs text-red-600">{t(`checkSets.${errors.name}`)}</p>}
                        </div>
                        {ownerType === 'tenant' && (
                            <div>
                                <label htmlFor="check-set-meant-for" className="mb-1 block text-xs font-semibold text-gray-600">{t('checkSets.meant_for_label')}</label>
                                <input
                                    id="check-set-meant-for"
                                    data-testid="check-set-meant-for"
                                    type="text"
                                    maxLength={LIMITS.meantFor}
                                    value={draft.meantFor}
                                    placeholder={t('checkSets.meant_for_placeholder')}
                                    onChange={(e) => patch({ meantFor: e.target.value.replace(/[<>]/g, '') })}
                                    className={`${input} ${showErrors && errors.meantFor ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                />
                                {showErrors && errors.meantFor && <p className="mt-1 text-xs text-red-600">{t(`checkSets.${errors.meantFor}`)}</p>}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">{t('checkSets.staff_may_record')}</p>
                                <p className="mt-0.5 text-xs text-gray-500">{t('checkSets.staff_may_record_hint')}</p>
                            </div>
                            <ToggleSwitch id="check-set-staff" on={draft.staffMayRecord} onToggle={() => patch({ staffMayRecord: !draft.staffMayRecord })} />
                        </div>
                        {ownerType !== 'tenant' && (
                            <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">{t('checkSets.required_every_visit')}</p>
                                    <p className="mt-0.5 text-xs text-gray-500">{t('checkSets.required_every_visit_hint')}</p>
                                </div>
                                <ToggleSwitch id="check-set-required" on={draft.requiredEveryVisit} onToggle={() => patch({ requiredEveryVisit: !draft.requiredEveryVisit })} />
                            </div>
                        )}
                    </div>

                    <section>
                        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                            <div>
                                <h3 className="text-sm font-bold text-gray-800">{t('checkSets.fields_heading')}</h3>
                                <p className="text-xs text-gray-500">{t('checkSets.fields_hint')}</p>
                            </div>
                            <span className="text-xs text-gray-400">{draft.fields.length}/{LIMITS.fieldsMax}</span>
                        </div>
                        {!isNew && (
                            <p className="mb-2 flex items-center gap-1.5 text-[11px] text-gray-500">
                                <Info className="h-3.5 w-3.5 text-[#2D8A8A]" aria-hidden="true" />
                                {t('checkSets.version_note')}
                            </p>
                        )}
                        {showErrors && errors.fields && (
                            <p data-testid="check-set-fields-error" className="mb-2 text-xs text-red-600">{t(`checkSets.${errors.fields}`)}</p>
                        )}

                        <ol className="flex flex-col gap-2" data-testid="check-set-fields">
                            {draft.fields.map((f, index) => {
                                const Icon = FIELD_TYPE_ICON[f.type] || FIELD_TYPE_ICON.text;
                                const labelErr = fieldErr(index, 'label');
                                const unitErr = fieldErr(index, 'unit');
                                const optionsErr = fieldErr(index, 'options');
                                const srvErr = serverErrors[index];
                                const choice = f.type === 'select' || f.type === 'multi';
                                return (
                                    <li key={f.uid} data-testid={`check-set-field-${index}`} className={`rounded-xl border bg-white p-3 shadow-sm ${labelErr || unitErr || optionsErr || srvErr ? 'border-red-200' : 'border-gray-100'}`}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A]" aria-hidden="true">
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <input
                                                type="text"
                                                data-testid={`check-set-field-${index}-label`}
                                                aria-label={t('checkSets.field_label')}
                                                maxLength={LIMITS.label}
                                                value={f.label}
                                                placeholder={t('checkSets.field_label_placeholder')}
                                                onChange={(e) => patchField(index, { label: e.target.value.replace(/[<>]/g, '') })}
                                                className={`min-w-[140px] flex-1 ${input} ${labelErr ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                            />
                                            <select
                                                data-testid={`check-set-field-${index}-type`}
                                                aria-label={t('checkSets.field_type')}
                                                value={f.type}
                                                onChange={(e) => patchField(index, { type: e.target.value })}
                                                className="tt-select w-40 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#4ABDBD] focus:outline-none"
                                            >
                                                {FIELD_TYPES.map((type) => <option key={type} value={type}>{t(`checkSets.type_${type}`)}</option>)}
                                            </select>
                                            {f.type === 'number' && (
                                                <input
                                                    type="text"
                                                    data-testid={`check-set-field-${index}-unit`}
                                                    aria-label={t('checkSets.field_unit')}
                                                    maxLength={LIMITS.unit}
                                                    value={f.unit}
                                                    placeholder={t('checkSets.field_unit_placeholder')}
                                                    onChange={(e) => patchField(index, { unit: e.target.value.replace(/[<>]/g, '') })}
                                                    className={`w-32 ${input} ${unitErr ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                                />
                                            )}
                                            <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`check-set-field-${index}-required`}
                                                    checked={f.required}
                                                    onChange={(e) => patchField(index, { required: e.target.checked })}
                                                    className="h-4 w-4 accent-[#4ABDBD]"
                                                />
                                                {t('checkSets.field_required')}
                                            </label>
                                            <div className="ml-auto flex items-center gap-0.5">
                                                <button type="button" data-testid={`check-set-field-${index}-up`} onClick={() => moveField(index, -1)} disabled={index === 0} aria-label={t('checkSets.move_up')} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30">
                                                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                                <button type="button" data-testid={`check-set-field-${index}-down`} onClick={() => moveField(index, 1)} disabled={index === draft.fields.length - 1} aria-label={t('checkSets.move_down')} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30">
                                                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                                <button type="button" data-testid={`check-set-field-${index}-remove`} onClick={() => removeField(index)} aria-label={t('checkSets.remove_field')} className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>

                                        {choice && (
                                            <div className="mt-2 rounded-xl border border-dashed border-gray-200 p-2">
                                                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('checkSets.field_options')}</p>
                                                <div className="flex flex-wrap items-center gap-1.5" data-testid={`check-set-field-${index}-options`}>
                                                    {(f.options || []).map((o) => (
                                                        <span key={o} className="inline-flex items-center gap-1 rounded-full border border-[#4ABDBD]/30 bg-[#4ABDBD]/10 px-2 py-0.5 text-xs font-medium text-[#2D8A8A]">
                                                            {o}
                                                            <button
                                                                type="button"
                                                                aria-label={t('checkSets.remove_option', { option: o })}
                                                                data-testid={`check-set-field-${index}-option-remove-${o}`}
                                                                onClick={() => removeOption(index, o)}
                                                                className="rounded-full p-0.5 hover:bg-[#4ABDBD]/20"
                                                            >
                                                                <X className="h-3 w-3" aria-hidden="true" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                    <span className="inline-flex min-w-[200px] flex-1 items-center gap-1">
                                                        <input
                                                            type="text"
                                                            data-testid={`check-set-field-${index}-option-input`}
                                                            aria-label={t('checkSets.option_placeholder')}
                                                            maxLength={LIMITS.option}
                                                            value={optionInputs[f.uid] || ''}
                                                            placeholder={t('checkSets.option_placeholder')}
                                                            onChange={(e) => setOptionInputs((prev) => ({ ...prev, [f.uid]: e.target.value.replace(/[<>]/g, '') }))}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    addOption(index);
                                                                }
                                                            }}
                                                            className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:border-[#4ABDBD] focus:outline-none"
                                                        />
                                                        <button
                                                            type="button"
                                                            data-testid={`check-set-field-${index}-option-add`}
                                                            onClick={() => addOption(index)}
                                                            className="inline-flex items-center gap-1 rounded-lg bg-[#4ABDBD]/10 px-2 py-1 text-xs font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/20"
                                                        >
                                                            <Plus className="h-3 w-3" aria-hidden="true" />
                                                            {t('checkSets.add_option')}
                                                        </button>
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {[labelErr, unitErr, optionsErr, srvErr].filter(Boolean).map((msg) => (
                                            <p key={msg} data-testid={`check-set-field-${index}-error`} className="mt-1 text-xs text-red-600">{msg}</p>
                                        ))}
                                    </li>
                                );
                            })}
                        </ol>

                        <button
                            type="button"
                            data-testid="check-set-add-field"
                            onClick={addField}
                            disabled={draft.fields.length >= LIMITS.fieldsMax}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-dashed border-[#4ABDBD] px-3 py-2 text-sm font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10 disabled:opacity-50"
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                            {t('checkSets.add_field')}
                        </button>
                    </section>

                    {serverError && (
                        <p role="alert" data-testid="check-set-server-error" className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-700">
                            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {serverError}
                        </p>
                    )}
                    {showErrors && hasSetErrors(errors) && (
                        <p data-testid="check-set-fix-above" className="text-xs font-medium text-red-600">{t('checkSets.err_fix_above')}</p>
                    )}
                </div>

            </ModalShell>
        </div>,
        document.body
    );
}

export default CheckSetEditor;
